import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Filter, X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export interface BookingFilterOptions {
  customers?: Array<{ id: string; name: string; email: string }>;
  companies?: Array<{ id: string; name: string; slug: string }>;
  services?: Array<{ id: string; name: string; category?: string }>;
}

export interface BookingFilters {
  search: string;
  customerId?: string;
  companyId?: string;
  serviceId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  status?: string;
  sortBy: 'date' | 'amount' | 'created';
  sortOrder: 'asc' | 'desc';
}

interface BookingFiltersProps {
  filters: BookingFilters;
  onFiltersChange: (filters: BookingFilters) => void;
  options: BookingFilterOptions;
  showCustomerFilter?: boolean;
  showCompanyFilter?: boolean;
  showServiceFilter?: boolean;
  showStatusFilter?: boolean;
  showDateFilter?: boolean;
  showSorting?: boolean;
  className?: string;
}

export default function BookingFilters({
  filters,
  onFiltersChange,
  options,
  showCustomerFilter = false,
  showCompanyFilter = false,
  showServiceFilter = true,
  showStatusFilter = true,
  showDateFilter = true,
  showSorting = true,
  className = ""
}: BookingFiltersProps) {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  // Update active filters when filters change
  useEffect(() => {
    const active: string[] = [];
    if (filters.search) active.push('search');
    if (filters.customerId) active.push('customer');
    if (filters.companyId) active.push('company');
    if (filters.serviceId) active.push('service');
    if (filters.dateFrom || filters.dateTo) active.push('date');
    if (filters.status) active.push('status');
    setActiveFilters(active);
  }, [filters]);

  const updateFilter = (key: keyof BookingFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilter = (key: keyof BookingFilters) => {
    onFiltersChange({ ...filters, [key]: key === 'search' ? '' : undefined });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      search: '',
      sortBy: 'date',
      sortOrder: 'desc'
    });
  };

  const toggleSortOrder = () => {
    updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getSortIcon = () => {
    if (filters.sortOrder === 'asc') return <ArrowUp className="h-4 w-4" />;
    return <ArrowDown className="h-4 w-4" />;
  };


  return (
    <div className={cn("space-y-4 w-full overflow-x-hidden", className)}>
      {/* Search and Quick Filters */}
      <div className="flex flex-col sm:flex-row gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <Input
            placeholder="Search bookings..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full"
          />
        </div>
        
        {showSorting && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto min-w-0">
            <div className="flex-1 min-w-0">
              <Select value={filters.sortBy} onValueChange={(value: any) => updateFilter('sortBy', value)}>
                <SelectTrigger className="w-full sm:w-32 min-w-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Service Date</SelectItem>
                  <SelectItem value="amount">Amount</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSortOrder}
              className="px-3 shrink-0"
            >
              {getSortIcon()}
            </Button>
          </div>
        )}
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-3 w-full">
        {/* Customer Filter */}
        {showCustomerFilter && options.customers && (
          <Select value={filters.customerId || 'all'} onValueChange={(value) => updateFilter('customerId', value === 'all' ? undefined : value)}>
            <SelectTrigger className="w-full sm:w-48 min-w-0">
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {options.customers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Company Filter */}
        {showCompanyFilter && options.companies && (
          <Select value={filters.companyId || 'all'} onValueChange={(value) => updateFilter('companyId', value === 'all' ? undefined : value)}>
            <SelectTrigger className="w-full sm:w-48 min-w-0">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {options.companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Service Filter */}
        {showServiceFilter && options.services && (
          <Select value={filters.serviceId || 'all'} onValueChange={(value) => updateFilter('serviceId', value === 'all' ? undefined : value)}>
            <SelectTrigger className="w-full sm:w-48 min-w-0">
              <SelectValue placeholder="All Services" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {options.services.map((service) => (
                <SelectItem key={service.id} value={service.id}>
                  {service.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Status Filter */}
        {showStatusFilter && (
          <Select value={filters.status || 'all'} onValueChange={(value) => updateFilter('status', value === 'all' ? undefined : value)}>
            <SelectTrigger className="w-full sm:w-40 min-w-0">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Date Filter */}
        {showDateFilter && (
          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs text-gray-600">Da (data prenotazione ≥)</label>
                <Input
                  type="date"
                  value={filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : undefined;
                    updateFilter('dateFrom', date);
                  }}
                  className="w-full sm:w-40"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <label className="text-xs text-gray-600">A (data prenotazione ≤)</label>
                <Input
                  type="date"
                  value={filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : undefined;
                    updateFilter('dateTo', date);
                  }}
                  className="w-full sm:w-40"
                />
              </div>
            {(filters.dateFrom || filters.dateTo) && (
              <div className="flex flex-col gap-1">
                <label className="text-xs text-transparent">Clear</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    updateFilter('dateFrom', undefined);
                    updateFilter('dateTo', undefined);
                  }}
                  className="h-10 px-3"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Clear All Button */}
        {activeFilters.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.search && (
            <Badge variant="secondary" className="gap-1">
              Search: {filters.search}
              <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('search')} />
            </Badge>
          )}
          {filters.customerId && (
            <Badge variant="secondary" className="gap-1">
              Customer: {options.customers?.find(c => c.id === filters.customerId)?.name}
              <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('customerId')} />
            </Badge>
          )}
          {filters.companyId && (
            <Badge variant="secondary" className="gap-1">
              Company: {options.companies?.find(c => c.id === filters.companyId)?.name}
              <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('companyId')} />
            </Badge>
          )}
          {filters.serviceId && (
            <Badge variant="secondary" className="gap-1">
              Service: {options.services?.find(s => s.id === filters.serviceId)?.name}
              <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('serviceId')} />
            </Badge>
          )}
          {filters.status && (
            <Badge variant="secondary" className="gap-1">
              Status: {filters.status}
              <X className="h-3 w-3 cursor-pointer" onClick={() => clearFilter('status')} />
            </Badge>
          )}
          {(filters.dateFrom || filters.dateTo) && (
            <Badge variant="secondary" className="gap-1">
              Date: {filters.dateFrom && filters.dateTo 
                ? `${format(filters.dateFrom, 'MMM d')} - ${format(filters.dateTo, 'MMM d')}`
                : filters.dateFrom 
                  ? `From ${format(filters.dateFrom, 'MMM d')}`
                  : `Until ${format(filters.dateTo, 'MMM d')}`
              }
              <X className="h-3 w-3 cursor-pointer" onClick={() => {
                clearFilter('dateFrom');
                clearFilter('dateTo');
              }} />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
